/**
 * How the app names the signed-in account and its role, wherever it shows
 * them: the account pill and its panel in the app bar from `sm` up, and the
 * account block in the phone's More drawer. One source, so a name or a role
 * word never reads differently on a tablet and on a phone.
 *
 * Update when a role id is added (see docs/access-control.md) or the account
 * payload gains a better name field.
 */
import { ROLES } from '@/config/roles.js';

/** The role word for an account: "Student" for `user`, else the id in Title Case. */
export const formatRoleLabel = (roleValue: string | undefined): string => {
  if (!roleValue) return '';
  if (roleValue === ROLES.USER) return 'Student';
  return roleValue
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

interface NamedAccount {
  displayName?: unknown;
  name?: unknown;
  email?: unknown;
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

/**
 * The name to show for an account: its DeepThink display name; else the
 * login's name, unless that is the email address (email-and-password logins
 * put it there); else the part of the email before the @.
 */
export const accountDisplayName = (
  account: NamedAccount | null | undefined
): string => {
  const displayName = text(account?.displayName);
  if (displayName) return displayName;
  const name = text(account?.name);
  if (name && !name.includes('@')) return name;
  const email = text(account?.email) ?? name;
  return email ? email.split('@')[0] : 'User';
};
